// src/pages/merchant/Subscription.js
// Page Marchand : abonnement courant + choix de pack (multivendeur)
// Maxula

import React, { useState, useEffect, useCallback } from 'react';
import { FiCheck, FiStar, FiZap, FiClock, FiAward, FiCreditCard, FiMessageCircle, FiHome, FiX, FiCopy, FiUpload, FiChevronRight, FiLock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { planAPI, subscriptionAPI, merchantAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { waNumber } from '../../utils/whatsapp';

const fmt = (v) => (typeof v === 'number' ? v.toLocaleString('fr-FR') : v);

const TYPE_LABELS = {
  merchant: 'Marchand',
  promoteur: 'Promoteur immobilier',
  agence: 'Agence'
};

// Cycles de facturation (mensuel / trimestriel / semestriel / annuel)
const CYCLES = [
  { k: 'monthly',    label: 'Mensuel',     unit: 'mois',     adj: 'mensuel',     months: 1,  field: 'priceMonthly' },
  { k: 'quarterly',  label: 'Trimestriel', unit: 'trimestre', adj: 'trimestriel', months: 3,  field: 'priceQuarterly' },
  { k: 'semiannual', label: 'Semestriel',  unit: 'semestre', adj: 'semestriel',  months: 6,  field: 'priceSemiannual' },
  { k: 'yearly',     label: 'Annuel',      unit: 'an',       adj: 'annuel',      months: 12, field: 'priceYearly' }
];
const cycleInfo = (k) => CYCLES.find((c) => c.k === k) || CYCLES[0];

// Prix effectif d'un cycle : utilise le prix défini, sinon repli = prix mensuel x nb de mois
// (évite l'affichage "0 MAD" en trimestriel/semestriel quand seul le mensuel est saisi).
const cyclePrice = (plan, cI) => {
  if (!plan || plan.isFree) return 0;
  const explicit = Number(plan[cI.field] || 0);
  if (explicit > 0) return explicit;
  return Math.round(Number(plan.priceMonthly || 0) * (cI.months || 1));
};

const limitLabel = (n, unit) => {
  if (n === -1) return `${unit} illimité${unit.endsWith('s') ? '' : 's'}`;
  return `${n} ${unit}`;
};

const MerchantSubscription = () => {
  const [plans, setPlans] = useState([]);
  const [info, setInfo] = useState(null); // { current, limits, merchant, history }
  const [cycle, setCycle] = useState('monthly');
  const [businessType, setBusinessType] = useState('merchant');
  const [confirmType, setConfirmType] = useState(null); // type en attente de confirmation
  const [payMethod, setPayMethod] = useState('virement');
  const [loading, setLoading] = useState(true);
  const [subscribingId, setSubscribingId] = useState(null);
  const [showPlans, setShowPlans] = useState(true);
  const [modalPlan, setModalPlan] = useState(null);
  const [payStep, setPayStep] = useState('method'); // method | card | virement
  const [virementSub, setVirementSub] = useState(null); // { id, ref, amount }
  const [virementTab, setVirementTab] = useState('instructions'); // instructions | proof
  const [proofFile, setProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [card, setCard] = useState({ name: '', number: '', exp: '', cvc: '' });
  const [processingCard, setProcessingCard] = useState(false);
  const { settings } = useSettings() || {};

  // Replier la grille des packs par defaut si un pack PAYANT est deja actif
  useEffect(() => {
    const cur = info && info.current;
    if (!cur || cur.status !== 'active') { setShowPlans(true); return; }
    const cp = (plans || []).find((p) => cur.planId === p._id || (cur.planId && cur.planId._id === p._id));
    const isFree = cp ? !!cp.isFree : !!(cur.planSnapshot && cur.planSnapshot.isFree);
    setShowPlans(isFree);
  }, [info, plans]);

  const load = useCallback(async (type) => {
    try {
      setLoading(true);
      const [pRes, mRes] = await Promise.all([
        planAPI.getPublic(type),
        subscriptionAPI.getMine()
      ]);
      setPlans(pRes.data?.data || []);
      const data = mRes.data?.data || null;
      setInfo(data);
      if (data?.merchant?.businessType) setBusinessType(data.merchant.businessType);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des abonnements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(businessType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTypeChange = async (type) => {
    setBusinessType(type);
    try {
      await merchantAPI.updateProfile({ businessType: type });
    } catch (e) {
      // pas bloquant
    }
    load(type);
  };

  const handleSubscribe = async (plan, method) => {
    try {
      setSubscribingId(plan._id);
      const res = await subscriptionAPI.subscribe({
        planId: plan._id,
        billingCycle: cycle,
        paymentMethod: plan.isFree ? 'free' : method
      });
      const d = res.data?.data;
      if (!d?.requiresPayment) {
        toast.success(`Pack "${plan.name}" activé !`);
      } else if (method === 'whatsapp') {
        const wa = waNumber(settings?.support?.whatsapp);
        const cycleLabel = cycleInfo(cycle).adj;
        const txt = encodeURIComponent(
          `Bonjour, je souhaite payer mon abonnement:\nPack: ${plan.name}\nCycle: ${cycleLabel}\nMontant: ${fmt(d.amount)} MAD\nRéf: ${d.subscription?.paymentRef || ''}`
        );
        if (wa) {
          window.open(`https://wa.me/${wa}?text=${txt}`, '_blank', 'noopener');
          toast.info('Finalisez le paiement sur WhatsApp. L\'admin activera votre pack.');
        } else {
          toast.warn('Aucun numéro WhatsApp configuré. Contactez le support.');
        }
      } else if (d?.gatewayPayload?.redirectUrl || d?.gatewayPayload?.url) {
        const url = d.gatewayPayload.redirectUrl || d.gatewayPayload.url;
        toast.info('Redirection vers le paiement…');
        window.location.href = url;
        return;
      } else if (d?.gatewayPayload?.paymentUrl && d?.gatewayPayload?.params) {
        // Passerelle CMI/Payzone : soumission auto d'un formulaire POST signé
        toast.info('Redirection vers la page de paiement sécurisée…');
        const f = document.createElement('form');
        f.method = 'POST';
        f.action = d.gatewayPayload.paymentUrl;
        Object.entries(d.gatewayPayload.params).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = typeof v === 'object' ? JSON.stringify(v) : String(v);
          f.appendChild(input);
        });
        document.body.appendChild(f);
        f.submit();
        return;
      } else if (method === 'cmi' || method === 'payzone') {
        toast.warn(
          `Le paiement par carte (${method.toUpperCase()}) n'est pas encore configuré sur le serveur. ` +
          `Choisissez « Virement bancaire » ou « Payer via WhatsApp » : l'admin activera votre pack après réception.`
        );
      } else {
        toast.success(
          `Demande enregistrée (${fmt(d.amount)} MAD). Payez par virement puis l'admin activera votre pack.`
        );
      }
      load(businessType);
      setModalPlan(null);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erreur lors de la souscription';
      toast.error(msg);
    } finally {
      setSubscribingId(null);
    }
  };

  const openPayModal = (plan) => {
    if (plan.isFree) { handleSubscribe(plan, 'free'); return; }
    setModalPlan(plan);
    setPayStep('method');
    setVirementSub(null);
    setVirementTab('instructions');
    setProofFile(null);
    setCard({ name: '', number: '', exp: '', cvc: '' });
  };

  const closePayModal = () => {
    setModalPlan(null);
    setPayStep('method');
    setVirementSub(null);
    setProofFile(null);
  };

  const copyText = async (txt) => {
    try { await navigator.clipboard.writeText(String(txt || '')); toast.info('Copié'); } catch (e) {}
  };

  // Virement : crée l'abonnement en attente pour obtenir la référence, puis montre les instructions
  const startManual = async (plan, method) => {
    try {
      setSubscribingId(plan._id);
      const res = await subscriptionAPI.subscribe({ planId: plan._id, billingCycle: cycle, paymentMethod: method });
      const d = res.data?.data;
      setVirementSub({ id: d?.subscription?.id, ref: d?.subscription?.paymentRef, amount: d?.amount });
      setVirementTab('instructions');
      setPayStep('manual');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la création de la demande');
    } finally {
      setSubscribingId(null);
    }
  };

  // Carte (CMI / PayZone) : déclenche le flux passerelle existant (redirection sécurisée)
  const payByCard = async (plan, method) => {
    setProcessingCard(true);
    try {
      await handleSubscribe(plan, method);
    } finally {
      setProcessingCard(false);
    }
  };

  const submitProof = async () => {
    if (!proofFile) { toast.warn('Sélectionnez le fichier de preuve.'); return; }
    if (!virementSub?.id) { toast.warn('Référence introuvable.'); return; }
    try {
      setUploadingProof(true);
      const fd = new FormData();
      fd.append('proof', proofFile);
      await subscriptionAPI.uploadProof(virementSub.id, fd);
      toast.success('Preuve envoyée. Activation après validation (24-48h ouvrées).');
      closePayModal();
      load(businessType);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'envoi de la preuve');
    } finally {
      setUploadingProof(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const current = info?.current;
  const limits = info?.limits;
  const currentPlanObj = (plans || []).find((p) => current && (current.planId === p._id || (current.planId && current.planId._id === p._id)));
  const currentIsFree = currentPlanObj ? !!currentPlanObj.isFree : !!(current && current.planSnapshot && current.planSnapshot.isFree);
  const currentMonthly = currentPlanObj ? Number(currentPlanObj.priceMonthly || 0) : Number((current && current.planSnapshot && current.planSnapshot.priceMonthly) || 0);
  const hasActivePaid = !!current && current.status === 'active' && !currentIsFree;
  const methodAvailable = (id) => {
    if (id === 'whatsapp') return !!(settings?.support?.whatsapp);
    // CMI / PayZone toujours proposees ; si la passerelle n'est pas configuree,
    // le paiement renvoie un message clair et l'utilisateur peut basculer sur une autre methode.
    return true;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Mon Abonnement</h1>
        <p className="text-gray-500 mt-1">Choisissez le pack adapté à votre activité et débloquez plus de fonctionnalités.</p>
      </div>

      {/* Abonnement courant + quota d'usage */}
      {(() => {
        const used = Number(limits?.dealsUsed || 0);
        const max = limits?.maxDeals;
        const unlimited = max === -1 || max == null;
        const pct = unlimited ? 100 : Math.min(100, Math.round((used / Math.max(1, max)) * 100));
        const barColor = !unlimited && pct >= 90 ? 'bg-red-400' : !unlimited && pct >= 70 ? 'bg-amber-300' : 'bg-white';
        const planName = current?.planSnapshot?.name || 'Pack gratuit';
        const status = current?.status || 'free';
        const statusLabel = status === 'active' ? 'Actif' : status === 'pending' ? 'En attente' : status === 'free' ? 'Gratuit' : status;
        return (
          <div className="mb-8 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-500 text-white p-6 shadow-lg">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="text-white/80 text-sm">Votre pack actuel</div>
                <div className="text-2xl font-bold flex items-center gap-2 mt-1">
                  <FiAward /> {planName}
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20">{statusLabel}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-8">
                <div>
                  <div className="text-white/70 text-xs">Commission</div>
                  <div className="text-lg font-bold">{limits?.commissionRate ?? 0}%</div>
                </div>
                <div>
                  <div className="text-white/70 text-xs">Mise en avant</div>
                  <div className="text-lg font-bold">{limitLabel(limits?.featuredDeals ?? 0, 'deals')}</div>
                </div>
                {current?.endDate && (
                  <div>
                    <div className="text-white/70 text-xs flex items-center gap-1"><FiClock size={12} /> Expire le</div>
                    <div className="text-lg font-bold">{new Date(current.endDate).toLocaleDateString('fr-FR')}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Barre de quota deals */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-white/80">Deals publiés</span>
                <span className="font-semibold">{unlimited ? `${used} / illimité` : `${used} / ${max}`}</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/25 overflow-hidden">
                <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              {!unlimited && used >= max && (
                <div className="text-xs text-white/95 mt-2">Quota atteint — passez à un pack supérieur pour publier plus de deals.</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Barre de contrôles : type + cycle */}
      <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Type de compte (segmenté) */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Type de compte</div>
          <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1">
            {Object.keys(TYPE_LABELS).map((t) => (
              <button
                key={t}
                onClick={() => { if (t !== businessType) setConfirmType(t); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  businessType === t ? 'bg-primary-600 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-400">Détermine les offres et commissions disponibles.</p>
        </div>

        {/* Cycle de facturation */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Facturation</div>
          <div className="inline-flex flex-wrap items-center rounded-xl border border-gray-200 bg-white p-1">
            {CYCLES.map((c) => (
              <button
                key={c.k}
                onClick={() => setCycle(c.k)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${cycle === c.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>


      {hasActivePaid && !showPlans ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowPlans(true)}
            className="px-6 py-3 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
          >
            Changer de pack
          </button>
        </div>
      ) : (
      <>
      {hasActivePaid && (
        <div className="flex justify-end mb-3">
          <button type="button" onClick={() => setShowPlans(false)} className="text-sm text-gray-500 hover:text-gray-700 underline">Masquer les packs</button>
        </div>
      )}
      {/* Grille des packs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
        {plans.map((plan) => {
          const cI = cycleInfo(cycle);
          const price = cyclePrice(plan, cI);
          const monthlyEquiv = (!plan.isFree && cI.months > 1 && price) ? Math.round(price / cI.months) : null;
          const savings = (!plan.isFree && cI.months > 1 && plan.priceMonthly && price)
            ? Math.max(0, Math.round((1 - price / (plan.priceMonthly * cI.months)) * 100)) : 0;
          const isCurrent = current && (current.planId === plan._id || current.planId?._id === plan._id);
          const popular = plan.isPopular;
          return (
            <div
              key={plan._id}
              className={`relative rounded-2xl bg-white flex flex-col transition ${
                popular ? 'border-2 border-primary-500 shadow-xl md:-translate-y-2' : 'border border-gray-200 hover:shadow-md'
              }`}
            >
              {popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary-600 text-white text-xs font-semibold flex items-center gap-1 shadow">
                  <FiStar size={12} /> Le plus populaire
                </div>
              )}

              <div className="p-6 pb-4">
                <div className="text-lg font-bold" style={{ color: plan.color || undefined }}>{plan.name}</div>
                <p className="text-sm text-gray-500 mt-1 min-h-[40px]">{plan.description}</p>

                <div className="mt-4">
                  {plan.isFree ? (
                    <div className="text-3xl font-extrabold text-gray-900">Gratuit</div>
                  ) : (
                    <>
                      <div className="flex items-end gap-1">
                        <span className="text-4xl font-extrabold text-gray-900">{fmt(price)}</span>
                        <span className="text-gray-500 mb-1.5 text-sm">MAD / {cI.unit}</span>
                      </div>
                      {monthlyEquiv != null && (
                        <div className="text-xs text-gray-400 mt-1">
                          soit ~{fmt(monthlyEquiv)} MAD / mois {savings > 0 && <span className="text-green-600 font-semibold">· économisez {savings}%</span>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="px-6 pb-6 flex flex-col flex-1">
                <ul className="space-y-2.5 text-sm flex-1 border-t border-gray-100 pt-4">
                  <li className="flex items-center gap-2 text-gray-700"><FiCheck className="text-green-600 shrink-0" /> {limitLabel(plan.maxDeals, 'deals')}</li>
                  <li className="flex items-center gap-2 text-gray-700"><FiCheck className="text-green-600 shrink-0" /> {limitLabel(plan.maxPhotos, 'photos')} / deal</li>
                  <li className="flex items-center gap-2 text-gray-700"><FiCheck className="text-green-600 shrink-0" /> Commission {plan.commissionRate}%</li>
                  <li className="flex items-center gap-2 text-gray-700"><FiZap className="text-amber-500 shrink-0" /> {limitLabel(plan.featuredDeals, 'mises en avant')}</li>
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600"><FiCheck className="text-gray-400 shrink-0" /> {f}</li>
                  ))}
                </ul>

                <button
                  disabled={isCurrent || subscribingId === plan._id || (plan.isFree && hasActivePaid)}
                  onClick={() => openPayModal(plan)}
                  className={`mt-6 w-full py-3 rounded-xl font-semibold transition ${
                    isCurrent
                      ? 'bg-green-50 text-green-700 border border-green-200 cursor-default'
                      : popular
                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-600/20'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {isCurrent ? '✓ Pack actuel' : subscribingId === plan._id ? '...' : plan.isFree ? (hasActivePaid ? (<><FiLock className="inline mr-1" size={14} />Pack par défaut</>) : 'Activer gratuitement') : (hasActivePaid ? (Number(plan.priceMonthly || 0) > currentMonthly ? 'Mettre à niveau' : 'Rétrograder') : 'Souscrire')}
                </button>
                {plan.isFree && hasActivePaid && (
                  <p className="mt-2 text-xs text-gray-400 text-center">Repli automatique à la fin de votre pack</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {plans.length === 0 && (
        <div className="text-center text-gray-500 py-12">Aucun pack disponible pour le moment.</div>
      )}
      </>
      )}

      {/* Réassurance */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: FiCheck, t: 'Sans engagement', d: 'Changez ou arrêtez votre pack à tout moment.' },
          { icon: FiZap, t: 'Activation rapide', d: 'Packs gratuits immédiats, payants validés rapidement.' },
          { icon: FiMessageCircle, t: 'Support dédié', d: 'Une question ? Notre équipe vous accompagne.' }
        ].map((x, i) => {
          const Icon = x.icon;
          return (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 flex items-start gap-3">
              <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0"><Icon size={16} /></span>
              <div>
                <div className="font-semibold text-gray-900 text-sm">{x.t}</div>
                <div className="text-xs text-gray-500">{x.d}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de paiement multi-etapes */}
      {modalPlan && (() => {
        const plan = modalPlan;
        const cI = cycleInfo(cycle);
        const price = cyclePrice(plan, cI);
        const bank = settings?.bankTransfer || {};
        const cycleLabel = cI.unit;
        const mmNumber = settings?.mobileMoney?.number || settings?.support?.whatsapp || settings?.support?.phone || '';
        const beneficiary = bank.accountHolder || settings?.siteName || 'Maxula';
        const methodOptions = [
          { id: 'payzone', label: 'PayZone (Maroc)', desc: 'Cartes Maroc + Mobile Money via PayZone', icon: FiCreditCard, show: methodAvailable('payzone') },
          { id: 'virement', label: 'Virement ou versement (banque / CashPlus / Wafacash)', desc: 'Virement bancaire OU depot especes en agence (banque, CashPlus, Wafacash, Tashilat) au profit du compte. Validation apres verification du recu.', icon: FiHome, show: true }
        ].filter((o) => o.show);
        const payAmount = fmt(virementSub?.amount ?? price);
        let manualRows = [];
        let manualNotice = '';
        if (payMethod === 'mobilemoney') {
          manualRows = [
            { k: 'Beneficiaire', v: beneficiary },
            { k: 'Numero / Wallet', v: mmNumber || '-', mono: true },
            { k: 'Operateurs', v: 'inwi money / Orange Money / CIH Pay' },
            { k: 'Montant', v: payAmount + ' MAD' },
            { k: 'Reference', v: virementSub?.ref || '-', mono: true }
          ];
          manualNotice = 'Envoyez ' + payAmount + ' MAD via inwi money / Orange Money / CIH Pay au ' + (mmNumber || '') + ', en mentionnant la reference ' + (virementSub?.ref || '') + '. Televersez ensuite la capture de confirmation.';
        } else {
          manualRows = [
            { k: 'Beneficiaire', v: beneficiary },
            { k: 'Banque', v: bank.bankName || '-' },
            { k: 'Versement especes', v: 'Agence bancaire / CashPlus / Wafacash / Tashilat' },
            { k: 'RIB', v: bank.rib || '-', mono: true },
            { k: 'IBAN', v: bank.iban || '-', mono: true },
            { k: 'SWIFT / BIC', v: bank.swift || '-', mono: true },
            { k: 'Reference (a indiquer imperativement)', v: virementSub?.ref || '-', mono: true }
          ];
          manualNotice = 'Effectuez un VIREMENT bancaire OU un VERSEMENT en especes (agence bancaire, CashPlus, Wafacash ou Tashilat) de ' + payAmount + ' MAD au profit du compte ci-dessus, en indiquant la reference ' + (virementSub?.ref || '') + '. Conservez le recu et televersez-le : l\'admin verifie et active votre pack (24-48h ouvrees).';
        }
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closePayModal}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between px-6 py-4 border-b">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {payStep === 'method' && 'Choisir un moyen de paiement'}
                    {payStep === 'card' && 'Paiement par carte'}
                    {payStep === 'manual' && (payMethod === 'mobilemoney' ? 'Paiement Mobile Money' : 'Virement bancaire ou versement en agence')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">{plan.name} - <span className="font-bold text-gray-700">{fmt(price)} MAD</span> / {cycleLabel}</p>
                </div>
                <button type="button" onClick={closePayModal} className="text-gray-400 hover:text-gray-700" aria-label="Fermer"><FiX size={20} /></button>
              </div>

              {payStep === 'method' && (
                <div className="p-5 space-y-3">
                  {methodOptions.map((m) => {
                    const Icon = m.icon;
                    return (
                      <button key={m.id} type="button"
                        onClick={() => { setPayMethod(m.id); if (m.id === 'cmi' || m.id === 'payzone') setPayStep('card'); else startManual(plan, m.id); }}
                        disabled={subscribingId === plan._id}
                        className="w-full flex items-center gap-4 text-left rounded-xl border border-gray-200 p-4 hover:border-primary-400 hover:bg-primary-50/40 transition disabled:opacity-60">
                        <span className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 shrink-0"><Icon size={20} /></span>
                        <span className="flex-1">
                          <span className="block font-semibold text-gray-900">{m.label}</span>
                          <span className="block text-sm text-gray-500">{m.desc}</span>
                        </span>
                        <FiChevronRight className="text-gray-300 shrink-0" size={20} />
                      </button>
                    );
                  })}
                  <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-3 text-xs text-gray-500 mt-2">
                    <FiLock size={14} className="shrink-0" /> Paiement 100% securise. Votre abonnement ne sera active qu'apres confirmation du paiement.
                  </div>
                </div>
              )}

              {payStep === 'card' && (
                <div className="p-6 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-400 mr-1">Moyens acceptes :</span>
                    {['VISA', 'Mastercard', 'Apple Pay', 'Google Pay'].map((b) => (
                      <span key={b} className="px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-[11px] font-semibold text-gray-600">{b}</span>
                    ))}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Titulaire de la carte</label>
                    <input type="text" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} placeholder="NOM Prenom" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Numero de carte</label>
                    <input type="text" inputMode="numeric" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value.replace(/[^0-9 ]/g, '').slice(0, 19) })} placeholder="0000 0000 0000 0000" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 font-mono focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Expiration</label>
                      <input type="text" inputMode="numeric" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value.replace(/[^0-9/]/g, '').slice(0, 5) })} placeholder="MM/AA" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">CVC</label>
                      <input type="text" inputMode="numeric" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })} placeholder="123" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-3 text-xs text-gray-500">
                    <FiLock size={14} className="shrink-0" /> Vos donnees sont transmises a la page bancaire securisee ({payMethod === 'payzone' ? 'PayZone' : 'CMI'} 3D-Secure). Maxula ne stocke pas votre carte. Apple Pay / Google Pay peuvent s'afficher sur cette page selon votre appareil et votre banque.
                  </div>
                  <div className="flex items-center justify-between gap-3 pt-2">
                    <button type="button" onClick={() => setPayStep('method')} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100">Retour</button>
                    <button type="button" disabled={processingCard || !card.number || !card.exp || !card.cvc} onClick={() => payByCard(plan, payMethod)} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
                      {processingCard ? '...' : `Payer ${fmt(price)} MAD`}
                    </button>
                  </div>
                </div>
              )}

              {payStep === 'manual' && (
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button type="button" onClick={() => setVirementTab('instructions')} className={`py-2.5 rounded-xl text-sm font-semibold transition ${virementTab === 'instructions' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}>1. Instructions</button>
                    <button type="button" onClick={() => setVirementTab('proof')} className={`py-2.5 rounded-xl text-sm font-semibold transition ${virementTab === 'proof' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}>2. Preuve de paiement</button>
                  </div>
                  {virementTab === 'instructions' ? (
                    <>
                      <div className="rounded-xl border border-gray-200 divide-y">
                        {manualRows.map((row, i) => (
                          <div key={i} className="flex items-center justify-between gap-3 px-4 py-3">
                            <span className="text-sm text-gray-500">{row.k}</span>
                            <span className="flex items-center gap-2">
                              <span className={`text-sm font-semibold text-gray-900 ${row.mono ? 'font-mono' : ''} select-all`}>{row.v}</span>
                              {row.v && row.v !== '-' && (<button type="button" onClick={() => copyText(row.v)} className="text-gray-400 hover:text-primary-600" aria-label="Copier"><FiCopy size={15} /></button>)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        <strong>Important :</strong> {manualNotice}
                      </div>
                      <div className="flex items-center justify-end gap-3 mt-4">
                        <button type="button" onClick={closePayModal} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100">Fermer</button>
                        <button type="button" onClick={() => setVirementTab('proof')} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 inline-flex items-center gap-2"><FiUpload size={16} /> Uploader la preuve</button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">Televersez une capture ou un recu de votre paiement (image). Reference : <strong>{virementSub?.ref}</strong>.</p>
                      <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-6 cursor-pointer hover:border-primary-400 hover:bg-primary-50/40 transition">
                        <FiUpload size={22} className="text-gray-400" />
                        <span className="text-sm text-gray-600">{proofFile ? proofFile.name : 'Cliquez pour choisir le fichier'}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
                      </label>
                      <div className="flex items-center justify-between gap-3">
                        <button type="button" onClick={() => setVirementTab('instructions')} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100">Retour</button>
                        <button type="button" disabled={uploadingProof || !proofFile} onClick={submitProof} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 inline-flex items-center gap-2"><FiUpload size={16} /> {uploadingProof ? 'Envoi...' : 'Envoyer la preuve'}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {confirmType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmType(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-amber-50 text-amber-600">
                  <FiAward size={20} />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Changer le type de compte ?</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Votre type passera de <strong>{TYPE_LABELS[businessType]}</strong> à{' '}
                    <strong>{TYPE_LABELS[confirmType]}</strong>. Les offres d'abonnement, les commissions
                    et les fonctionnalités disponibles seront différentes.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50">
              <button
                type="button"
                onClick={() => setConfirmType(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => { const t = confirmType; setConfirmType(null); handleTypeChange(t); }}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-primary-600 hover:bg-primary-700"
              >
                Confirmer le changement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantSubscription;
